import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Navigate } from "react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { canAccessPendingVobo } from "../providers/commons/canAccess";
import type { AppRole } from "../types";
import type { PayableSummary, ReceivableSummary } from "./invoicingTypes";
import { PendingPayableRow, PendingReceivableRow } from "./VoboRows";

// Pending rows are few (Dirección clears them); one bounded page per section.
const PENDING_PAGE_SIZE = 100;

const PendingSection = <Row extends { id: string | number }>({
  resource,
  emptyKey,
  renderRow,
}: {
  resource: "receivables" | "payables";
  emptyKey: string;
  renderRow: (row: Row) => ReactNode;
}) => {
  const translate = useTranslate();
  const { data, isPending, error, refetch } = useGetList<Row>(resource, {
    filter: { status: "pendiente_vobo" },
    pagination: { page: 1, perPage: PENDING_PAGE_SIZE },
    sort: { field: "created_at", order: "ASC" },
  });
  if (isPending) return <ListRowsSkeleton rows={3} />;
  if (error) {
    return (
      <ListErrorState
        message={translate("crm.invoicing.error")}
        onRetry={() => refetch()}
      />
    );
  }
  if (!data || data.length === 0) {
    return <ListEmptyState title={translate(emptyKey)} />;
  }
  return <div className="space-y-3">{data.map(renderRow)}</div>;
};

/**
 * Almikia Epic E6 (TASK-040): "Pendientes de VoBo", direccion only. Two
 * sections: receivables and payables in `pendiente_vobo`. Every action calls
 * an RPC that re-checks the role, so the UI gate is only a convenience.
 */
export const VoboPendingPage = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  if (!identity) return null;
  if (!canAccessPendingVobo(identity.role as AppRole)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">
          {translate("crm.invoicing.vobo.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.invoicing.vobo.subtitle")}
        </p>
      </header>
      <Tabs defaultValue="receivables">
        <TabsList>
          <TabsTrigger value="receivables" className="min-h-11">
            {translate("crm.invoicing.vobo.tab_receivables")}
          </TabsTrigger>
          <TabsTrigger value="payables" className="min-h-11">
            {translate("crm.invoicing.vobo.tab_payables")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="receivables">
          <PendingSection<ReceivableSummary>
            resource="receivables"
            emptyKey="crm.invoicing.vobo.empty_receivables"
            renderRow={(row) => (
              <PendingReceivableRow key={row.id} receivable={row} />
            )}
          />
        </TabsContent>
        <TabsContent value="payables">
          <PendingSection<PayableSummary>
            resource="payables"
            emptyKey="crm.invoicing.vobo.empty_payables"
            renderRow={(row) => (
              <PendingPayableRow key={row.id} payable={row} />
            )}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

VoboPendingPage.path = "/vobo-pendientes";
